<template>
    <div class="home" :class="{ 'page-entering': isEntering, 'page-leaving': isLeaving }">
        <!-- Canvas页面过渡遮罩 -->
        <div class="page-transition-overlay"
            :class="{ 'active': showTransition, 'entering': isEntering, 'leaving': isLeaving }">
            <canvas ref="transitionCanvas" class="transition-canvas"></canvas>
        </div>

        <!-- 顶部标题区域 -->
        <header class="hero-section" v-show="showHero">
            <!-- 丰富的背景装饰元素 -->
            <div class="hero-background">
                <!-- 几何图形 -->
                <div class="floating-shape shape-1"></div>
                <div class="floating-shape shape-2"></div>
                <div class="floating-shape shape-3"></div>
                <div class="floating-shape shape-4"></div>
                <div class="floating-shape shape-5"></div>

                <!-- 网格背景 -->
                <div class="grid-pattern"></div>

                <!-- 光晕效果 -->
                <div class="glow-orb orb-1"></div>
                <div class="glow-orb orb-2"></div>
                <div class="glow-orb orb-3"></div>

                <!-- 粒子效果 -->
                <div class="particles">
                    <div v-for="n in 20" :key="n" class="particle" :style="getParticleStyle(n)"></div>
                </div>

                <!-- 装饰线条 -->
                <div class="deco-lines">
                    <div class="line line-1"></div>
                    <div class="line line-2"></div>
                    <div class="line line-3"></div>
                </div>
            </div>

            <div class="container">

                <div class="hero-content">
                    <!-- 头像设计 -->
                    <div class="hero-avatar">
                        <div class="avatar-container-wrapper">
                            <div class="avatar-glow"></div>
                            <div class="avatar-simple">
                                <img src="/mahiru-logo/logo2.svg" alt="Mahiru Logo" class="avatar-logo" />
                            </div>
                            <div class="avatar-ring"></div>
                        </div>
                    </div>

                    <!-- 主标题带打字效果 -->
                    <h1 class="blog-title">
                        <span class="title-line">MAHIRU的<span class="typing-text"
                                :class="'theme-color-' + currentThemeIndex">{{ currentThemeWord }}</span></span>
                    </h1>

                    <!-- 主题词指示器 -->
                    <div class="theme-indicators">
                        <span v-for="(word, index) in themeWords" :key="word" class="theme-dot"
                            :class="{ 'active': currentThemeIndex === index, ['theme-dot-color-' + index]: true }"
                            @click="setThemeWord(index)"></span>
                    </div>

                    <!-- 简化副标题 -->
                    <div class="subtitle-wrapper">
                        <div class="subtitle-decoration left"></div>
                        <p class="blog-subtitle">
                            ✦ 技术分享与学习记录 ✦
                        </p>
                        <div class="subtitle-decoration right"></div>
                    </div>

                    <!-- 统计信息 -->
                    <div class="hero-stats">
                        <div class="stat-item">
                            <div class="stat-number">{{ stats.articleCount }}</div>
                            <div class="stat-label">篇文章</div>
                        </div>
                        <div class="stat-divider"></div>
                        <div class="stat-item">
                            <div class="stat-number">{{ stats.tagCount }}</div>
                            <div class="stat-label">个标签</div>
                        </div>
                        <div class="stat-divider"></div>
                        <div class="stat-item">
                            <div class="stat-number">{{ formatNumber(stats.totalWords) }}</div>
                            <div class="stat-label">总字数</div>
                        </div>
                    </div>

                    <!-- 社交链接和快捷操作 -->
                    <div class="hero-actions">
                        <button class="action-btn primary" @click="startReading">
                            <BookOpenIcon :size="18" class="btn-icon" />
                            开始阅读
                        </button>
                        <button class="action-btn secondary" @click="goToAuth">
                            <EditIcon :size="18" class="btn-icon" />
                            登录注册
                        </button>
                    </div>

                    <!-- 社交媒体链接 -->
                    <div class="social-links">
                        <a href="https://github.com/mizorehe70dayo" target="_blank" class="social-link" title="GitHub">
                            <GithubIcon :size="22" />
                        </a>
                        <a href="#" target="_blank" class="social-link" title="Twitter">
                            <TwitterIcon :size="22" />
                        </a>
                        <a href="#" target="_blank" class="social-link" title="LinkedIn">
                            <LinkedinIcon :size="22" />
                        </a>
                    </div>
                </div>
            </div>

            <!-- Canvas波浪效果 -->
            <div class="wave-container">
                <canvas ref="waveCanvas" class="wave-canvas"></canvas>
            </div>
        </header>

        <!-- 主要内容区域 -->
        <main class="main-content" v-if="false">
            <!-- 顶部导航栏（文章区域） -->
            <a-layout-header class="nav-header">
                <div class="nav-inner">
                    <!-- 左：Logo + 名称（返回首页） -->
                    <button class="brand-btn" @click="goHome" title="返回首页">
                        <svg class="brand-mark" viewBox="0 0 64 64" aria-hidden="true">
                            <defs>
                                <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0%" stop-color="#667eea" />
                                    <stop offset="100%" stop-color="#764ba2" />
                                </linearGradient>
                                <filter id="soft" x="-20%" y="-20%" width="140%" height="140%">
                                    <feGaussianBlur in="SourceGraphic" stdDeviation="0.6" />
                                </filter>
                            </defs>
                            <circle cx="32" cy="32" r="28" fill="url(#g1)" opacity="0.95" />
                            <circle cx="27" cy="26" r="12" fill="#ffffff" opacity="0.16" filter="url(#soft)" />
                            <circle cx="42" cy="22" r="2" fill="#fff" opacity="0.9" />
                            <path d="M16 42 L24 24 L32 34 L40 22 L48 42" fill="none" stroke="#ffffff" stroke-width="3.2"
                                stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <span class="brand-title">Mahiru Journal</span>
                    </button>

                    <!-- 中：chips + 搜索 -->
                    <div class="nav-center-wrap">
                        <div v-if="categories.length || tags.length" class="nav-chips">
                            <a-tag v-for="cat in categories.slice(0, 6)" :key="'c-' + cat.id"
                                :color="selectedCategoryId === cat.id ? 'geekblue' : 'default'" class="chip"
                                @click="onCategoryClick(cat)">{{ cat.name }}</a-tag>
                            <a-tag v-for="tag in displayTags.slice(0, 8)" :key="'t-' + tag.id"
                                :color="selectedTagIds.includes(tag.id) ? 'purple' : 'default'" class="chip"
                                @click="onTagClick(tag)">{{ tag.name }}</a-tag>
                        </div>
                        <a-input-search v-model:value="searchQuery" size="large" class="nav-search"
                            placeholder="搜索文章、标签或分类" @search="handleSearch" @change="handleSearch" />
                    </div>

                    <!-- 右：作者信息 -->
                    <div class="nav-right">
                        <a-avatar size="large">M</a-avatar>
                        <span class="author-text">Mahiru</span>
                    </div>
                </div>
            </a-layout-header>
            <!-- 其余文章区域内容保留但不渲染 -->
        </main>
    </div>
</template>

<script>
// 文章浏览改为独立路由 /browse
// 已迁移到 Supabase：不再使用本地 IndexedDB 数据库
import LazyImage from '../components/LazyImage.vue'
import CachedImage from '../components/CachedImage.vue'
import { useArticleCacheStore } from '../stores/articleCache.js'
import { useBrowseStateStore } from '../stores/browseState.js'
import configCacheService from '../services/configCacheService.js'
import { GithubIcon, TwitterIcon, LinkedinIcon, ChevronUpIcon, SearchIcon, XIcon, FolderIcon, TagIcon, BookOpenIcon, EditIcon, ChevronDownIcon } from 'lucide-vue-next'
import { Layout, Tag, Input, Avatar } from 'ant-design-vue'

export default {
    name: 'Home',
    components: {
        LazyImage,
        CachedImage,
        GithubIcon,
        TwitterIcon,
        LinkedinIcon,
        ChevronUpIcon,
        SearchIcon,
        XIcon,
        FolderIcon,
        TagIcon,
        BookOpenIcon,
        EditIcon,
        ChevronDownIcon,
        // Ant Design Vue
        'a-layout': Layout,
        'a-layout-header': Layout.Header,
        'a-tag': Tag,
        'a-input-search': Input.Search,
        'a-avatar': Avatar
    },
    setup() {
        // 获取store实例
        const articleCacheStore = useArticleCacheStore()
        const browseStateStore = useBrowseStateStore()

        return {
            articleCacheStore,
            browseStateStore
        }
    },
    data() {
        return {
            articles: [],
            categories: [],
            tags: [],
            showAllTags: false,
            // 统计数据
            stats: {
                articleCount: 0,
                tagCount: 0,
                totalWords: 0
            },
            blogConfig: {},
            siteConfig: {}, // 网站配置
            configLoaded: false, // 配置加载状态
            loading: true,
            error: null,
            searchQuery: '',
            filteredArticles: [],
            // 主题词切换相关
            themeWords: ['笔记', '文章', '分享'],
            currentThemeIndex: 0,
            currentThemeWord: '笔记',
            typingTimer: null,
            isTyping: false,
            typingDirection: 'forward',
            typingIndex: 0,
            typingSpeed: 150,
            typingPause: 2000,
            // 新增：筛选状态
            selectedCategoryId: null,
            selectedTagIds: [],
            // 懒加载相关
            totalArticles: 0,
            isLazyLoading: false,
            hasMoreContent: true,
            // 控制hero显示
            showHero: true,
            // 页面过渡状态
            isEntering: false,
            isLeaving: false,
            showTransition: false,

            // Canvas波浪系统
            waveCanvas: null,
            waveCtx: null,
            transitionCanvas: null,
            transitionCtx: null,
            animationFrame: null,
            transitionAnimationFrame: null,

            // 波浪参数 - 优化为更自然的层次
            waves: [
                { amplitude: 25, frequency: 0.008, phase: 0, speed: 0.01, opacity: 0.12, baseY: 0.75 },
                { amplitude: 20, frequency: 0.012, phase: Math.PI * 0.3, speed: 0.008, opacity: 0.16, baseY: 0.82 },
                { amplitude: 15, frequency: 0.015, phase: Math.PI * 0.7, speed: 0.012, opacity: 0.14, baseY: 0.88 },
                { amplitude: 10, frequency: 0.018, phase: Math.PI * 1.2, speed: 0.015, opacity: 0.10, baseY: 0.94 }
            ],

            // 过渡动画参数
            transitionProgress: 0,
            transitionDirection: 'entering' // 'entering' or 'leaving'
        }
    },
    computed: {
        // 显示的标签列表（最多10个或全部）
        displayTags() {
            return this.showAllTags ? this.tags : this.tags.slice(0, 10)
        },
        // 是否有更多标签
        hasMoreTags() {
            return this.tags.length > 10
        }
    },
    async mounted() {
        // 初始化Canvas
        await this.$nextTick()
        this.initCanvases()

        // 页面进入动画
        this.playEnterAnimation()

        // 加载网站配置（可能已经在main.js中预加载了）
        await this.loadSiteConfig()

        // 加载统计数据
        await this.loadStats()

        // 清理过期状态
        this.browseStateStore.smartCleanup()

        // 数据加载完成后恢复状态
        this.restoreHomeState()

        // 启动打字动画
        this.startTypingAnimation()
    },
    beforeUnmount() {
        // 清理动画
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame)
        }
        if (this.transitionAnimationFrame) {
            cancelAnimationFrame(this.transitionAnimationFrame)
        }

        // 清理事件监听器
        window.removeEventListener('resize', this.handleResize)

        // 保存当前状态
        this.saveCurrentState()
    },
    methods: {
        // 加载网站配置
        async loadSiteConfig() {
            try {
                console.log('🏠 首页加载网站配置...')

                // 尝试从缓存获取配置（可能已在main.js中预加载）
                const cachedConfig = configCacheService.getFromCache('site.config')
                if (cachedConfig) {
                    console.log('✅ 使用缓存的网站配置:', cachedConfig)
                    this.siteConfig = cachedConfig
                    this.configLoaded = true
                    this.applyConfigToPage()
                    return
                }

                // 如果缓存中没有，重新加载
                console.log('📡 重新加载网站配置...')
                const config = await configCacheService.preloadSiteConfig()
                this.siteConfig = config
                this.configLoaded = true
                this.applyConfigToPage()

            } catch (error) {
                console.error('❌ 加载网站配置失败:', error)
                // 使用默认配置
                this.siteConfig = configCacheService.getDefaultSiteConfig()
                this.configLoaded = true
                this.applyConfigToPage()
            }
        },

        // 将配置应用到页面
        applyConfigToPage() {
            if (!this.siteConfig) return

            try {
                // 更新页面标题
                if (this.siteConfig.title) {
                    document.title = this.siteConfig.title
                }

                // 应用背景（如果还没有应用）
                if (this.siteConfig.backgroundUrl && this.siteConfig.backgroundType) {
                    configCacheService.applyBackground(this.siteConfig.backgroundUrl, this.siteConfig.backgroundType)
                }

                console.log('✅ 网站配置已应用到首页')
            } catch (error) {
                console.error('❌ 应用网站配置失败:', error)
            }
        },

        // 主题词切换动画
        startTypingAnimation() {
            if (this.typingTimer) clearTimeout(this.typingTimer)
            const currentWord = this.themeWords[this.currentThemeIndex]
            if (this.typingDirection === 'forward') {
                if (this.typingIndex < currentWord.length) {
                    this.currentThemeWord = currentWord.substring(0, this.typingIndex + 1)
                    this.typingIndex++
                    this.typingTimer = setTimeout(this.startTypingAnimation, this.typingSpeed)
                } else {
                    this.typingDirection = 'backward'
                    this.typingTimer = setTimeout(this.startTypingAnimation, this.typingPause)
                }
            } else {
                if (this.typingIndex > 0) {
                    this.typingIndex--
                    this.currentThemeWord = currentWord.substring(0, this.typingIndex)
                    this.typingTimer = setTimeout(this.startTypingAnimation, this.typingSpeed / 2)
                } else {
                    this.typingDirection = 'forward'
                    this.currentThemeIndex = (this.currentThemeIndex + 1) % this.themeWords.length
                    this.typingTimer = setTimeout(this.startTypingAnimation, this.typingSpeed)
                }
            }
        },
        setThemeWord(index) {
            if (this.typingTimer) clearTimeout(this.typingTimer)
            this.currentThemeIndex = index
            this.typingIndex = 0
            this.typingDirection = 'forward'
            this.startTypingAnimation()
        },
        async openArticle(article) {
            console.log(`💾 保存首页状态，准备跳转到文章 ${article.id}`)
            this.saveCurrentState()
            this.$router.push({ name: 'ArticleDetail', params: { id: article.id.toString() } })
        },
        formatNumber(num) {
            if (num >= 1000) {
                return (num / 1000).toFixed(1) + 'k'
            }
            return num.toString()
        },
        async loadStats() {
            try {
                const { getStats } = await import('../services/dataService.js')
                const statsData = await getStats()
                if (statsData) {
                    this.stats = statsData
                }
            } catch (error) {
                console.error('加载统计数据失败:', error)
            }
        },
        async startReading() {
            await this.playLeaveAnimation()
            this.$router.push('/browse')
        },
        goHome() {
            this.showHero = true
            this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
        },
        async goToAuth() {
            await this.playLeaveAnimation()
            this.$router.push('/auth')
        },
        toggleShowAllTags() {
            this.showAllTags = !this.showAllTags
            this.browseStateStore.saveTagsExpandState(this.showAllTags)
        },
        onCategoryClick(category) {
            if (this.selectedCategoryId === category.id) this.selectedCategoryId = null
            else {
                this.selectedCategoryId = category.id
                this.selectedTagIds = []
                this.searchQuery = ''
            }
            this.applyFilters()
            this.browseStateStore.saveFilters({ searchQuery: this.searchQuery, selectedCategoryId: this.selectedCategoryId, selectedTagIds: [...this.selectedTagIds] })
        },
        onTagClick(tag) {
            const index = this.selectedTagIds.indexOf(tag.id)
            if (index > -1) this.selectedTagIds.splice(index, 1)
            else this.selectedTagIds.push(tag.id)
            if (this.selectedTagIds.length > 0) {
                this.selectedCategoryId = null
                this.searchQuery = ''
            }
            this.applyFilters()
            this.browseStateStore.saveFilters({ searchQuery: this.searchQuery, selectedCategoryId: this.selectedCategoryId, selectedTagIds: [...this.selectedTagIds] })
        },
        applyFilters() {
            let filtered = [...this.articles]
            if (this.selectedCategoryId) filtered = filtered.filter(a => a.category_id === this.selectedCategoryId)
            if (this.selectedTagIds.length > 0) {
                filtered = filtered.filter(a => Array.isArray(a.tag_ids) && this.selectedTagIds.some(id => a.tag_ids.includes(id)))
            }
            if (this.searchQuery.trim()) {
                const q = this.searchQuery.toLowerCase()
                filtered = filtered.filter(a => {
                    const titleMatch = a.title.toLowerCase().includes(q)
                    const excerptMatch = a.excerpt.toLowerCase().includes(q)
                    const tagsMatch = this.getTagsByIds(a.tag_ids).some(t => t.name.toLowerCase().includes(q))
                    const cat = this.getCategoryById(a.category_id)
                    const catMatch = cat && cat.name.toLowerCase().includes(q)
                    return titleMatch || excerptMatch || tagsMatch || catMatch
                })
            }
            this.filteredArticles = filtered
        },
        handleSearch() {
            if (this.searchQuery.trim()) {
                this.selectedCategoryId = null
                this.selectedTagIds = []
            }
            this.applyFilters()
            this.browseStateStore.saveFilters({ searchQuery: this.searchQuery, selectedCategoryId: this.selectedCategoryId, selectedTagIds: [...this.selectedTagIds] })
        },
        clearSearch() {
            this.searchQuery = ''
            this.applyFilters()
        },
        getCategoryById(categoryId) { return this.categories.find(cat => cat.id === categoryId) },
        getTagsByIds(tagIds) { if (!Array.isArray(tagIds)) return []; return this.tags.filter(tag => tagIds.includes(tag.id)) },
        getTagById(tagId) { return this.tags.find(tag => tag.id === tagId) },
        getRelativeTime(d) {
            const now = new Date(); const t = new Date(d); const ms = now - t
            const sec = Math.floor(ms / 1e3), min = Math.floor(sec / 60), hr = Math.floor(min / 60), day = Math.floor(hr / 24), wk = Math.floor(day / 7), mon = Math.floor(day / 30), yr = Math.floor(day / 365)
            if (sec < 60) return '刚刚'; if (min < 60) return `${min}分钟前`; if (hr < 24) return `${hr}小时前`; if (day < 7) return `${day}天前`; if (wk < 4) return `${wk}周前`; if (mon < 12) return `${mon}个月前`; return `${yr}年前`
        },
        clearCategoryFilter() { this.selectedCategoryId = null; this.applyFilters() },
        removeTagFilter(tagId) { const i = this.selectedTagIds.indexOf(tagId); if (i > -1) this.selectedTagIds.splice(i, 1); this.applyFilters() },
        clearAllFilters() { this.selectedCategoryId = null; this.selectedTagIds = []; this.searchQuery = ''; this.applyFilters() },
        saveCurrentState() {
            const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop
            this.browseStateStore.saveHomeState({
                scrollPosition: currentScrollPosition,
                filters: { searchQuery: this.searchQuery, selectedCategoryId: this.selectedCategoryId, selectedTagIds: [...this.selectedTagIds] },
                showAllTags: this.showAllTags
            })
        },
        restoreHomeState() {
            const savedState = this.browseStateStore.getHomeState()
            if (!savedState) return
            if (savedState.filters) {
                this.searchQuery = savedState.filters.searchQuery || ''
                this.selectedCategoryId = savedState.filters.selectedCategoryId || null
                this.selectedTagIds = [...(savedState.filters.selectedTagIds || [])]
                this.applyFilters()
            }
            this.showAllTags = savedState.showAllTags || false
            this.$nextTick(() => this.browseStateStore.applyDelayedScrollRestore(savedState.scrollPosition, 10))
        },
        getParticleStyle(index) {
            const size = Math.random() * 4 + 2
            const left = Math.random() * 100
            const top = Math.random() * 100
            const animationDelay = Math.random() * 10
            const animationDuration = Math.random() * 20 + 15

            return {
                width: `${size}px`,
                height: `${size}px`,
                left: `${left}%`,
                top: `${top}%`,
                animationDelay: `${animationDelay}s`,
                animationDuration: `${animationDuration}s`
            }
        },

        // 页面进入动画 - 波浪退去效果
        playEnterAnimation() {
            this.isEntering = true
            this.showTransition = true
            this.transitionDirection = 'entering'

            // 启动波浪退去动画
            this.startWaveEnterTransition()

            // 动画完成后隐藏遮罩
            setTimeout(() => {
                this.showTransition = false
                this.isEntering = false
            }, 1200)
        },

        // 页面离开动画 - 使用底部波浪上升效果
        playLeaveAnimation() {
            return new Promise((resolve) => {
                this.isLeaving = true
                this.showTransition = true
                this.transitionDirection = 'leaving'

                // 启动波浪上升动画
                this.startWaveTransition()

                setTimeout(() => {
                    resolve()
                }, 1200)
            })
        },

        // 初始化Canvas
        initCanvases() {
            // 初始化底部波浪Canvas
            this.waveCanvas = this.$refs.waveCanvas
            if (this.waveCanvas) {
                this.waveCtx = this.waveCanvas.getContext('2d')
                this.resizeWaveCanvas()
                this.startWaveAnimation()
            }

            // 初始化过渡Canvas
            this.transitionCanvas = this.$refs.transitionCanvas
            if (this.transitionCanvas) {
                this.transitionCtx = this.transitionCanvas.getContext('2d')
                this.resizeTransitionCanvas()
            }

            // 监听窗口大小变化
            window.addEventListener('resize', this.handleResize)
        },

        // 处理窗口大小变化
        handleResize() {
            this.resizeWaveCanvas()
            this.resizeTransitionCanvas()
        },

        // 调整波浪Canvas大小
        resizeWaveCanvas() {
            if (!this.waveCanvas) return

            const container = this.waveCanvas.parentElement
            const rect = container.getBoundingClientRect()
            const dpr = window.devicePixelRatio || 1

            this.waveCanvas.width = rect.width * dpr
            this.waveCanvas.height = rect.height * dpr
            this.waveCanvas.style.width = rect.width + 'px'
            this.waveCanvas.style.height = rect.height + 'px'

            this.waveCtx.scale(dpr, dpr)
        },

        // 调整过渡Canvas大小
        resizeTransitionCanvas() {
            if (!this.transitionCanvas) return

            const dpr = window.devicePixelRatio || 1

            this.transitionCanvas.width = window.innerWidth * dpr
            this.transitionCanvas.height = window.innerHeight * dpr
            this.transitionCanvas.style.width = window.innerWidth + 'px'
            this.transitionCanvas.style.height = window.innerHeight + 'px'

            this.transitionCtx.scale(dpr, dpr)
        },

        // 开始波浪动画
        startWaveAnimation() {
            const animate = () => {
                this.drawWaves()
                this.animationFrame = requestAnimationFrame(animate)
            }
            animate()
        },

        // 绘制波浪
        drawWaves() {
            if (!this.waveCtx || !this.waveCanvas) return

            const width = this.waveCanvas.clientWidth
            const height = this.waveCanvas.clientHeight

            // 清除画布
            this.waveCtx.clearRect(0, 0, width, height)

            // 绘制每一层波浪
            this.waves.forEach((wave, index) => {
                this.drawWave(wave, width, height, index)
                // 更新相位
                wave.phase += wave.speed
            })
        },

        // 绘制单层波浪
        drawWave(wave, width, height, layerIndex) {
            this.waveCtx.beginPath()
            this.waveCtx.moveTo(0, height)

            // 创建波浪路径
            for (let x = 0; x <= width; x += 2) {
                const y = wave.baseY * height +
                    Math.sin(x * wave.frequency + wave.phase) * wave.amplitude +
                    Math.sin(x * wave.frequency * 0.5 + wave.phase * 0.7) * wave.amplitude * 0.3

                if (x === 0) {
                    this.waveCtx.moveTo(x, y)
                } else {
                    this.waveCtx.lineTo(x, y)
                }
            }

            // 闭合路径
            this.waveCtx.lineTo(width, height)
            this.waveCtx.lineTo(0, height)
            this.waveCtx.closePath()

            // 设置填充样式
            this.waveCtx.fillStyle = `rgba(255, 255, 255, ${wave.opacity})`
            this.waveCtx.fill()
        },

        // 开始波浪离开过渡动画 - 从底部现有波浪开始上升
        startWaveTransition() {
            if (!this.transitionCtx) return

            this.transitionProgress = 0
            const startTime = Date.now()

            const animate = () => {
                const elapsed = Date.now() - startTime
                this.transitionProgress = Math.min(elapsed / 1200, 1) // 1.2秒完成

                this.drawWaveTransition()

                if (this.transitionProgress < 1) {
                    this.transitionAnimationFrame = requestAnimationFrame(animate)
                }
            }

            animate()
        },

        // 开始波浪进入过渡动画 - 从顶部退去到底部
        startWaveEnterTransition() {
            if (!this.transitionCtx) return

            this.transitionProgress = 0
            const startTime = Date.now()

            const animate = () => {
                const elapsed = Date.now() - startTime
                this.transitionProgress = Math.min(elapsed / 1200, 1) // 1.2秒完成

                this.drawWaveEnterTransition()

                if (this.transitionProgress < 1) {
                    this.transitionAnimationFrame = requestAnimationFrame(animate)
                }
            }

            animate()
        },

        // 绘制波浪过渡效果 - 从底部自然上升
        drawWaveTransition() {
            if (!this.transitionCtx || !this.transitionCanvas) return

            const width = this.transitionCanvas.clientWidth
            const height = this.transitionCanvas.clientHeight

            // 清除画布
            this.transitionCtx.clearRect(0, 0, width, height)

            // 使用缓动函数让动画更自然
            const progress = this.easeInOutQuart(this.transitionProgress)

            // 创建从底部到顶部的渐变
            const gradient = this.transitionCtx.createLinearGradient(0, height, 0, 0)
            gradient.addColorStop(0, 'rgba(102, 126, 234, 1)')
            gradient.addColorStop(0.3, 'rgba(118, 75, 162, 0.98)')
            gradient.addColorStop(0.7, 'rgba(102, 126, 234, 0.95)')
            gradient.addColorStop(1, 'rgba(102, 126, 234, 0.8)')

            // 波浪从底部200px位置开始上升到覆盖整个屏幕
            const startY = height - 200 // 从底部200px开始
            const targetY = 0 // 目标是顶部
            const currentY = startY + (targetY - startY) * progress

            // 绘制多层波浪
            this.drawTransitionWaveLayer(width, height, currentY, progress, 0, gradient)
            this.drawTransitionWaveLayer(width, height, currentY + 30, progress, Math.PI * 0.3, 'rgba(255, 255, 255, 0.1)')
            this.drawTransitionWaveLayer(width, height, currentY + 60, progress, Math.PI * 0.7, 'rgba(255, 255, 255, 0.05)')

            // 添加动态泡沫效果
            this.drawTransitionFoam(width, height, currentY, progress)
        },

        // 绘制单层过渡波浪
        drawTransitionWaveLayer(width, height, baseY, progress, phaseOffset, fillStyle) {
            const amplitude = 40 + progress * 20 // 振幅随进度增加
            const frequency = 0.008 + progress * 0.002 // 频率略微增加
            const time = Date.now() * 0.001

            this.transitionCtx.beginPath()
            this.transitionCtx.moveTo(0, height)

            // 绘制波浪形状
            for (let x = 0; x <= width; x += 2) {
                const wave1 = Math.sin(x * frequency + time + phaseOffset) * amplitude
                const wave2 = Math.sin(x * frequency * 1.5 + time * 0.7 + phaseOffset) * amplitude * 0.3
                const y = baseY + wave1 + wave2

                this.transitionCtx.lineTo(x, y)
            }

            // 闭合路径
            this.transitionCtx.lineTo(width, height)
            this.transitionCtx.lineTo(0, height)
            this.transitionCtx.closePath()

            this.transitionCtx.fillStyle = fillStyle
            this.transitionCtx.fill()
        },

        // 绘制过渡泡沫效果
        drawTransitionFoam(width, height, waveY, progress) {
            const foamCount = Math.floor(6 + progress * 4)
            const time = Date.now() * 0.001

            for (let i = 0; i < foamCount; i++) {
                const x = (width / foamCount) * i + Math.sin(time * 0.5 + i) * 30
                const y = waveY - 20 + Math.sin(time * 0.8 + i) * 15
                const radius = 2 + Math.sin(time * 2 + i) * 2 + progress * 2
                const opacity = (0.2 + Math.sin(time * 1.2 + i) * 0.1) * (1 - progress * 0.3)

                this.transitionCtx.beginPath()
                this.transitionCtx.arc(x, y, radius, 0, Math.PI * 2)
                this.transitionCtx.fillStyle = `rgba(255, 255, 255, ${opacity})`
                this.transitionCtx.fill()
            }
        },

        // 绘制波浪进入过渡效果 - 从顶部退去到底部
        drawWaveEnterTransition() {
            if (!this.transitionCtx || !this.transitionCanvas) return

            const width = this.transitionCanvas.clientWidth
            const height = this.transitionCanvas.clientHeight

            // 清除画布
            this.transitionCtx.clearRect(0, 0, width, height)

            // 使用缓动函数让退去更自然
            const progress = this.easeInOutQuart(this.transitionProgress)

            // 创建从底部到顶部的渐变，但透明度随progress递减
            const gradient = this.transitionCtx.createLinearGradient(0, height, 0, 0)
            const baseOpacity = 1 - progress * 0.8 // 逐渐变透明
            gradient.addColorStop(0, `rgba(102, 126, 234, ${baseOpacity})`)
            gradient.addColorStop(0.3, `rgba(118, 75, 162, ${baseOpacity * 0.98})`)
            gradient.addColorStop(0.7, `rgba(102, 126, 234, ${baseOpacity * 0.95})`)
            gradient.addColorStop(1, `rgba(102, 126, 234, ${baseOpacity * 0.8})`)

            // 波浪从覆盖整个屏幕退去到底部200px位置
            const startY = 0 // 从顶部开始
            const targetY = height - 200 // 目标是底部200px
            const currentY = startY + (targetY - startY) * progress

            // 绘制多层波浪，振幅随progress减小
            this.drawEnterWaveLayer(width, height, currentY, progress, 0, gradient)
            this.drawEnterWaveLayer(width, height, currentY + 30, progress, Math.PI * 0.3, `rgba(255, 255, 255, ${0.1 * (1 - progress * 0.7)})`)
            this.drawEnterWaveLayer(width, height, currentY + 60, progress, Math.PI * 0.7, `rgba(255, 255, 255, ${0.05 * (1 - progress * 0.5)})`)

            // 添加退去的泡沫效果
            this.drawEnterFoam(width, height, currentY, progress)
        },

        // 绘制单层进入波浪
        drawEnterWaveLayer(width, height, baseY, progress, phaseOffset, fillStyle) {
            const amplitude = (60 - progress * 35) // 振幅随进度减小
            const frequency = (0.012 - progress * 0.004) // 频率随进度减小
            const time = Date.now() * 0.001

            this.transitionCtx.beginPath()
            this.transitionCtx.moveTo(0, height)

            // 绘制波浪形状
            for (let x = 0; x <= width; x += 2) {
                const wave1 = Math.sin(x * frequency + time + phaseOffset) * amplitude
                const wave2 = Math.sin(x * frequency * 1.5 + time * 0.7 + phaseOffset) * amplitude * 0.3
                const y = baseY + wave1 + wave2

                this.transitionCtx.lineTo(x, y)
            }

            // 闭合路径
            this.transitionCtx.lineTo(width, height)
            this.transitionCtx.lineTo(0, height)
            this.transitionCtx.closePath()

            this.transitionCtx.fillStyle = fillStyle
            this.transitionCtx.fill()
        },

        // 绘制进入泡沫效果
        drawEnterFoam(width, height, waveY, progress) {
            const foamCount = Math.floor(10 - progress * 6) // 泡沫数量递减
            const time = Date.now() * 0.001

            for (let i = 0; i < foamCount; i++) {
                const x = (width / foamCount) * i + Math.sin(time * 0.5 + i) * 20
                const y = waveY - 30 + Math.sin(time * 0.8 + i) * 20
                const radius = (4 - progress * 2) + Math.sin(time * 2 + i) * 1
                const opacity = (0.4 - progress * 0.3) + Math.sin(time * 1.2 + i) * 0.1

                if (radius > 0 && opacity > 0) {
                    this.transitionCtx.beginPath()
                    this.transitionCtx.arc(x, y, radius, 0, Math.PI * 2)
                    this.transitionCtx.fillStyle = `rgba(255, 255, 255, ${opacity})`
                    this.transitionCtx.fill()
                }
            }
        },

        // 缓动函数
        easeInOutCubic(t) {
            return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1
        },

        easeOutCubic(t) {
            return 1 - Math.pow(1 - t, 3)
        },

        easeInOutQuart(t) {
            return t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2
        }
    }
}
</script>

<style scoped>
/* 主页整体样式优化 */
.home {
    min-height: 100vh;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    position: relative;
    overflow-x: hidden;
    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 页面过渡状态 */
.home.page-entering {
    opacity: 0;
    transform: translateY(30px);
}

.home.page-leaving {
    opacity: 1;
    transform: translateY(0);
}

/* Canvas页面过渡遮罩 */
.page-transition-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    z-index: 9999;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.page-transition-overlay.active {
    opacity: 1;
}

.transition-canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

/* 进入动画 - 波浪从底部升起 */
.page-transition-overlay.entering .main-wave {
    animation: waveRiseIn 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
}

.page-transition-overlay.entering .deco-wave-1 {
    animation: waveRiseIn 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s forwards;
}

.page-transition-overlay.entering .deco-wave-2 {
    animation: waveRiseIn 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s forwards;
}

/* 离开动画 - 波浪卷走页面 */
.page-transition-overlay.leaving .main-wave {
    animation: waveSweepOut 0.8s cubic-bezier(0.55, 0.085, 0.68, 0.53) forwards;
}

.page-transition-overlay.leaving .deco-wave-1 {
    animation: waveSweepOut 0.8s cubic-bezier(0.55, 0.085, 0.68, 0.53) 0.05s forwards;
}

.page-transition-overlay.leaving .deco-wave-2 {
    animation: waveSweepOut 0.8s cubic-bezier(0.55, 0.085, 0.68, 0.53) 0.1s forwards;
}

/* 泡沫动画 */
.foam {
    animation: foamFloat 2s ease-in-out infinite;
}

.foam-1 {
    animation-delay: 0s;
}

.foam-2 {
    animation-delay: 0.5s;
}

.foam-3 {
    animation-delay: 1s;
}

.foam-4 {
    animation-delay: 1.5s;
}

@keyframes waveRiseIn {
    0% {
        d: path("M0,800 L0,800 C200,800 400,800 600,800 C800,800 1000,800 1200,800 L1200,800 Z");
    }

    50% {
        d: path("M0,800 L0,600 C200,550 400,500 600,520 C800,540 1000,580 1200,560 L1200,800 Z");
    }

    100% {
        d: path("M0,800 L0,400 C200,350 400,300 600,320 C800,340 1000,380 1200,360 L1200,800 Z");
    }
}

@keyframes waveSweepOut {
    0% {
        d: path("M0,800 L0,400 C200,350 400,300 600,320 C800,340 1000,380 1200,360 L1200,800 Z");
        transform: translateY(0) scale(1);
    }

    30% {
        d: path("M0,800 L0,200 C200,150 400,100 600,120 C800,140 1000,180 1200,160 L1200,800 Z");
        transform: translateY(-50px) scale(1.1);
    }

    70% {
        d: path("M0,800 L0,50 C200,20 400,10 600,15 C800,20 1000,30 1200,25 L1200,800 Z");
        transform: translateY(-150px) scale(1.2);
    }

    100% {
        d: path("M0,800 L0,0 C200,0 400,0 600,0 C800,0 1000,0 1200,0 L1200,800 Z");
        transform: translateY(-300px) scale(1.5);
    }
}

@keyframes foamFloat {

    0%,
    100% {
        transform: translateY(0) scale(1);
        opacity: 0.6;
    }

    50% {
        transform: translateY(-10px) scale(1.1);
        opacity: 1;
    }
}

.hero-section {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 2rem 1rem;
}

.hero-content {
    text-align: center;
    max-width: 800px;
    z-index: 2;
    position: relative;
}

.blog-title {
    font-size: clamp(2.5rem, 8vw, 4.5rem);
    font-weight: 800;
    color: rgba(255, 255, 255, 0.95);
    margin: 0 0 1rem;
    text-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    line-height: 1.1;
    letter-spacing: -0.02em;
}

/* 副标题装饰 */
.subtitle-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 2.5rem;
}

.subtitle-decoration {
    width: 40px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
    animation: shimmer 2s ease-in-out infinite;
}

.subtitle-decoration.left {
    animation-delay: 0s;
}

.subtitle-decoration.right {
    animation-delay: 1s;
}

.blog-subtitle {
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    font-weight: 400;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    position: relative;
    padding: 0 1rem;
}

.blog-subtitle::before,
.blog-subtitle::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 4px;
    height: 4px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 50%;
    transform: translateY(-50%);
    animation: twinkle 1.5s ease-in-out infinite;
}

.blog-subtitle::before {
    left: 0;
    animation-delay: 0s;
}

.blog-subtitle::after {
    right: 0;
    animation-delay: 0.75s;
}

@keyframes shimmer {

    0%,
    100% {
        opacity: 0.3;
    }

    50% {
        opacity: 1;
    }
}

@keyframes twinkle {

    0%,
    100% {
        opacity: 0.4;
        transform: translateY(-50%) scale(1);
    }

    50% {
        opacity: 1;
        transform: translateY(-50%) scale(1.2);
    }
}

/* 主题指示器样式 */
.theme-indicators {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin: 1.5rem 0 2rem;
}

.theme-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s ease;
    opacity: 0.6;
}

.theme-dot.active {
    opacity: 1;
    transform: scale(1.2);
}

.theme-dot-color-0 {
    background: linear-gradient(135deg, #FFD93D, #FF8A00);
}

.theme-dot-color-1 {
    background: linear-gradient(135deg, #667eea, #764ba2);
}

.theme-dot-color-2 {
    background: linear-gradient(135deg, #4ECDC4, #44A08D);
}

/* 统计信息样式 */
.hero-stats {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2rem;
    margin: 2.5rem 0;
    padding: 1.5rem 2rem;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.stat-item {
    text-align: center;
}

.stat-number {
    font-size: 2rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.95);
    line-height: 1;
}

.stat-label {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
    margin-top: 0.25rem;
}

.stat-divider {
    width: 1px;
    height: 2rem;
    background: rgba(255, 255, 255, 0.3);
}

/* 操作按钮样式 */
.hero-actions {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin: 2.5rem 0 2rem;
    flex-wrap: wrap;
}

.action-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.875rem 2rem;
    border-radius: 12px;
    font-weight: 600;
    font-size: 1rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: none;
    cursor: pointer;
    text-decoration: none;
}

.action-btn.primary {
    background: rgba(255, 255, 255, 0.95);
    color: #667eea;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.action-btn.primary:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.action-btn.secondary {
    background: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);
}

.action-btn.secondary:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-2px);
    border-color: rgba(255, 255, 255, 0.5);
}

/* 社交链接样式 */
.social-links {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;
}

.social-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.9);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.social-link:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
    color: rgba(255, 255, 255, 1);
    border-color: rgba(255, 255, 255, 0.4);
}

/* Canvas底部波浪效果 */
.wave-container {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 200px;
    overflow: hidden;
    pointer-events: none;
}

.wave-canvas {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

/* 背景装饰优化 */
.hero-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 1;
}

.floating-shape {
    position: absolute;
    backdrop-filter: blur(20px);
    animation: float 8s ease-in-out infinite;
}

.shape-1 {
    width: 100px;
    height: 100px;
    top: 15%;
    left: 8%;
    background: linear-gradient(45deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05));
    border-radius: 50%;
    animation-delay: 0s;
}

.shape-2 {
    width: 80px;
    height: 80px;
    top: 70%;
    right: 12%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2));
    border-radius: 20px;
    animation-delay: 2s;
    transform: rotate(45deg);
}

.shape-3 {
    width: 60px;
    height: 60px;
    bottom: 25%;
    left: 15%;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.08));
    border-radius: 50%;
    animation-delay: 4s;
}

.shape-4 {
    width: 40px;
    height: 40px;
    top: 40%;
    right: 25%;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.15));
    border-radius: 8px;
    animation-delay: 1s;
    transform: rotate(30deg);
}

.shape-5 {
    width: 90px;
    height: 90px;
    top: 10%;
    right: 8%;
    background: linear-gradient(225deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
    animation-delay: 3s;
}

/* 网格背景 */
.grid-pattern {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.3;
    background-image:
        linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
    background-size: 50px 50px;
    animation: gridMove 20s linear infinite;
}

@keyframes gridMove {
    0% {
        transform: translate(0, 0);
    }

    100% {
        transform: translate(50px, 50px);
    }
}

/* 光晕效果 */
.glow-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(40px);
    animation: glow 6s ease-in-out infinite alternate;
}

.orb-1 {
    width: 200px;
    height: 200px;
    top: 20%;
    left: 20%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.2), transparent);
    animation-delay: 0s;
}

.orb-2 {
    width: 150px;
    height: 150px;
    bottom: 30%;
    right: 20%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.15), transparent);
    animation-delay: 2s;
}

.orb-3 {
    width: 100px;
    height: 100px;
    top: 60%;
    left: 70%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1), transparent);
    animation-delay: 4s;
}

@keyframes glow {
    0% {
        opacity: 0.3;
        transform: scale(1);
    }

    100% {
        opacity: 0.8;
        transform: scale(1.2);
    }
}

/* 粒子效果 */
.particles {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.particle {
    position: absolute;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 50%;
    animation: particleFloat linear infinite;
}

@keyframes particleFloat {
    0% {
        transform: translateY(100vh) rotate(0deg);
        opacity: 0;
    }

    10% {
        opacity: 1;
    }

    90% {
        opacity: 1;
    }

    100% {
        transform: translateY(-100px) rotate(360deg);
        opacity: 0;
    }
}

/* 装饰线条 */
.deco-lines {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.line {
    position: absolute;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    animation: lineMove 8s ease-in-out infinite;
}

.line-1 {
    width: 200px;
    height: 2px;
    top: 25%;
    left: -200px;
    animation-delay: 0s;
}

.line-2 {
    width: 150px;
    height: 1px;
    top: 65%;
    right: -150px;
    animation-delay: 3s;
}

.line-3 {
    width: 100px;
    height: 1px;
    top: 45%;
    left: -100px;
    animation-delay: 6s;
}

@keyframes lineMove {
    0% {
        transform: translateX(0);
        opacity: 0;
    }

    50% {
        opacity: 1;
    }

    100% {
        transform: translateX(calc(100vw + 200px));
        opacity: 0;
    }
}

@keyframes float {

    0%,
    100% {
        transform: translateY(0px) rotate(0deg);
        opacity: 0.7;
    }

    50% {
        transform: translateY(-30px) rotate(180deg);
        opacity: 1;
    }
}

/* 响应式设计 */
@media (max-width: 768px) {
    .hero-stats {
        flex-direction: column;
        gap: 1rem;
        padding: 1.25rem 1.5rem;
    }

    .stat-divider {
        width: 2rem;
        height: 1px;
    }

    .hero-actions {
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
    }

    .action-btn {
        width: 100%;
        max-width: 280px;
        justify-content: center;
    }

    .social-links {
        gap: 0.75rem;
    }

    .social-link {
        width: 44px;
        height: 44px;
    }
}

@media (max-width: 480px) {
    .hero-section {
        padding: 1rem 0.75rem;
    }

    .avatar-simple {
        width: 100px;
        height: 100px;
    }

    .avatar-glow {
        width: 130px;
        height: 130px;
    }

    .avatar-ring {
        width: 120px;
        height: 120px;
    }

    .avatar-text {
        font-size: 28px;
    }

    .hero-stats {
        padding: 1rem 1.25rem;
        margin: 2rem 0;
    }

    .stat-number {
        font-size: 1.5rem;
    }

    .stat-label {
        font-size: 0.8rem;
    }

    .wave-container {
        height: 80px;
    }

    .subtitle-decoration {
        width: 20px;
    }
}

/* Canvas性能优化 */
.wave-canvas,
.transition-canvas {
    will-change: transform;
}

/* 减少动画复杂度在低性能设备上 */
@media (prefers-reduced-motion: reduce) {

    .floating-shape,
    .particle,
    .glow-orb,
    .line,
    .avatar-glow,
    .avatar-ring {
        animation: none;
    }

    .page-transition-overlay,
    .home {
        transition: none;
    }
}

/* 主题词颜色 - 纯色设计 */
.theme-color-0 {
    color: #10b981;
}

.theme-color-1 {
    color: #3b82f6;
}

.theme-color-2 {
    color: #8b5cf6;
}

/* 增强头像样式 */
.hero-avatar {
    position: relative;
    display: flex;
    justify-content: center;
    margin-bottom: 2rem;
}

.avatar-container-wrapper {
    position: relative;
    display: inline-block;
}

.avatar-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 160px;
    height: 160px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.2), transparent);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    filter: blur(20px);
    animation: avatarGlow 3s ease-in-out infinite alternate;
}

.avatar-simple {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.15));
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow:
        0 8px 32px rgba(0, 0, 0, 0.3),
        inset 0 2px 8px rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(15px);
    border: 3px solid rgba(255, 255, 255, 0.4);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    z-index: 2;
}

.avatar-ring {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 140px;
    height: 140px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    animation: rotateRing 10s linear infinite;
    z-index: 1;
}

.avatar-ring::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 50%;
    width: 8px;
    height: 8px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    transform: translateX(-50%);
}

.avatar-simple::before {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea, #764ba2);
    z-index: -1;
    opacity: 0;
    transition: opacity 0.4s ease;
}

.avatar-container-wrapper:hover .avatar-simple::before {
    opacity: 0.8;
}

.avatar-container-wrapper:hover .avatar-simple {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0.25));
    transform: scale(1.05) translateY(-2px);
    box-shadow:
        0 12px 40px rgba(0, 0, 0, 0.4),
        inset 0 2px 12px rgba(255, 255, 255, 0.3);
}

.avatar-logo {
    width: 80px;
    height: auto;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.avatar-container-wrapper:hover .avatar-logo {
    transform: scale(1.1);
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4));
}

@keyframes avatarGlow {
    0% {
        opacity: 0.5;
        transform: translate(-50%, -50%) scale(1);
    }

    100% {
        opacity: 0.8;
        transform: translate(-50%, -50%) scale(1.1);
    }
}

@keyframes rotateRing {
    0% {
        transform: translate(-50%, -50%) rotate(0deg);
    }

    100% {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}

/* 分类和标签区域 */
.taxonomy-section {
    margin: 2rem 0;
    padding: 1.5rem 0;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    backdrop-filter: blur(10px);
}

.section-title {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-bottom: 1rem;
    font-size: 1.1rem;
    font-weight: 600;
    color: #333333;
}

.title-icon {
    font-size: 1rem;
    color: #667eea;
}

/* 分类区域 */
.categories-section {
    margin-bottom: 1.5rem;
}

.categories-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 0.6rem;
}

.category-item {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    padding: 0.8rem;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(10px);
}

.category-item:hover {
    background: rgba(255, 255, 255, 1);
    border-color: rgba(102, 126, 234, 0.4);
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.category-item.selected {
    background: rgba(102, 126, 234, 0.2);
    border-color: rgba(102, 126, 234, 0.5);
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
}

.category-name {
    font-size: 0.95rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.3rem;
}

.category-description {
    font-size: 0.8rem;
    color: #666;
    line-height: 1.3;
}

/* 标签区域 */
.tags-section {
    margin-bottom: 1.5rem;
}

.tags-container {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.tags-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
}

.tag-item {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.3rem 0.6rem;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
    color: #667eea;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid rgba(102, 126, 234, 0.4);
    background: rgba(102, 126, 234, 0.15);
    position: relative;
    backdrop-filter: blur(5px);
}

.tag-item:hover {
    background: rgba(102, 126, 234, 0.25);
    border-color: rgba(102, 126, 234, 0.5);
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(102, 126, 234, 0.3);
}

.tag-item.selected {
    background: rgba(102, 126, 234, 0.95);
    color: white;
    border-color: rgba(102, 126, 234, 0.95);
    box-shadow: 0 3px 10px rgba(102, 126, 234, 0.4);
    transform: translateY(-1px);
}

.tag-name {
    position: relative;
}

.tag-count {
    background: rgba(255, 255, 255, 0.8);
    border-radius: 3px;
    padding: 0.1rem 0.3rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: #667eea;
}

.tag-item.selected .tag-count {
    background: rgba(255, 255, 255, 0.3);
    color: white;
}

.show-more-btn {
    align-self: flex-start;
    display: flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.4rem 0.8rem;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 6px;
    color: #667eea;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(10px);
}

.show-more-btn:hover {
    background: rgba(255, 255, 255, 1);
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    transform: translateY(-1px);
}

.btn-icon {
    font-size: 0.8rem;
    transition: transform 0.3s ease;
}

.show-more-btn:hover .btn-icon {
    transform: translateX(2px);
}

/* 响应式设计 */
@media (max-width: 768px) {
    .taxonomy-section {
        margin: 1.2rem 0;
        padding: 0.8rem 0;
    }

    .categories-grid {
        grid-template-columns: 1fr;
        gap: 0.5rem;
    }

    .category-item {
        padding: 0.7rem;
    }

    .tags-list {
        gap: 0.3rem;
    }

    .tag-item {
        padding: 0.25rem 0.5rem;
        font-size: 0.75rem;
    }

    .tag-count {
        padding: 0.1rem 0.25rem;
        font-size: 0.65rem;
    }

    .show-more-btn {
        padding: 0.3rem 0.7rem;
        font-size: 0.75rem;
    }
}

@media (max-width: 480px) {
    .section-title {
        font-size: 0.95rem;
    }

    .categories-section {
        margin-bottom: 1.2rem;
    }

    .category-item {
        padding: 0.6rem;
    }

    .category-name {
        font-size: 0.85rem;
    }

    .category-description {
        font-size: 0.75rem;
    }
}

/* 文章卡片新布局样式 */
.title-row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
}

.category {
    background: rgba(0, 0, 0, 0.2);
    padding: 0.2rem 0.6rem;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
    white-space: nowrap;
    flex-shrink: 0;
    backdrop-filter: blur(5px);
}

.card-title {
    margin: 0;
    flex: 1;
    line-height: 1.3;
}

.card-footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: 1rem;
    gap: 1rem;
}

.tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    flex: 1;
}

.tags .tag {
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 500;
    color: #667eea;
    background: rgba(102, 126, 234, 0.08);
    border: 1px solid rgba(102, 126, 234, 0.15);
}

.relative-time {
    font-size: 0.75rem;
    color: #666;
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;
    background: #f5f5f5;
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

/* 筛选状态样式 */
.filter-status {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1.2rem;
    padding: 0.6rem 0.8rem;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    color: #333;
    font-size: 0.85rem;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(10px);
}

.filter-info {
    display: flex;
    align-items: center;
    gap: 0.6rem;
}

.filter-label {
    color: #666;
}

.filter-tag {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.25rem 0.6rem;
    border-radius: 4px;
    background: rgba(102, 126, 234, 0.08);
    border: 1px solid rgba(102, 126, 234, 0.15);
}

.filter-tag.category-filter {
    color: #42b983;
    background: rgba(66, 185, 131, 0.08);
    border-color: rgba(66, 185, 131, 0.15);
}

.filter-tag.tag-filter {
    color: #667eea;
    background: rgba(102, 126, 234, 0.08);
    border-color: rgba(102, 126, 234, 0.15);
}

.remove-filter {
    background: none;
    border: none;
    color: #999;
    font-size: 0.85rem;
    cursor: pointer;
    padding: 0 0.1rem;
    line-height: 1;
    transition: color 0.2s ease;
}

.remove-filter:hover {
    color: #667eea;
}

.clear-all-filters {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 6px;
    padding: 0.3rem 0.8rem;
    color: #667eea;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(10px);
}

.clear-all-filters:hover {
    background: rgba(255, 255, 255, 1);
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    transform: translateY(-1px);
}

.filter-results {
    color: #666;
    font-size: 0.8rem;
}

@media (max-width: 768px) {
    .filter-status {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
        padding: 0.75rem 0.5rem;
    }

    .filter-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.4rem;
    }

    .filter-tag {
        padding: 0.3rem 0.6rem;
        font-size: 0.8rem;
    }

    .remove-filter {
        padding: 0 0.1rem;
    }

    .clear-all-filters {
        padding: 0.4rem 0.8rem;
        font-size: 0.75rem;
    }
}

@media (max-width: 480px) {
    .filter-status {
        font-size: 0.8rem;
    }

    .filter-results {
        font-size: 0.8rem;
    }
}

/* Lucide图标样式 */
.search-icon,
.title-icon,
.btn-icon {
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
}

.search-icon {
    color: #667eea;
}

.title-icon {
    color: #667eea;
}

.btn-icon {
    color: inherit;
}

.clear-btn {
    display: flex;
    align-items: center;
    justify-content: center;
}

.remove-filter {
    display: flex;
    align-items: center;
    justify-content: center;
}

.filter-tag {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.category,
.update-time,
.word-count,
.reading-time,
.tag {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.action-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.show-more-btn {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

/* 懒加载状态样式 */
.lazy-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    color: #667eea;
    font-size: 0.85rem;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 12px;
    backdrop-filter: blur(10px);
    margin: 1rem 0;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.loading-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(102, 126, 234, 0.2);
    border-top: 2px solid rgba(102, 126, 234, 0.8);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-right: 0.4rem;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.no-more-content {
    text-align: center;
    padding: 1.5rem;
    color: #667eea;
    font-size: 0.85rem;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 12px;
    backdrop-filter: blur(10px);
    margin: 1rem 0;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* 返回顶部按钮样式 */
.back-to-hero {
    position: fixed;
    bottom: 30px;
    right: 30px;
    z-index: 1000;
}

.back-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 12px;
    padding: 0.8rem 1.2rem;
    color: #667eea;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(15px);
}

.back-btn:hover {
    background: rgba(255, 255, 255, 1);
    border-color: rgba(102, 126, 234, 0.4);
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.back-btn .btn-icon {
    transition: transform 0.3s ease;
}

.back-btn:hover .btn-icon {
    transform: translateY(-1px);
}

/* 响应式设计 */
@media (max-width: 768px) {
    .back-to-hero {
        bottom: 20px;
        right: 20px;
    }

    .back-btn {
        padding: 0.6rem 1rem;
        font-size: 0.8rem;
    }
}

@media (max-width: 480px) {
    .back-to-hero {
        bottom: 15px;
        right: 15px;
    }

    .back-btn {
        padding: 0.5rem 0.8rem;
        font-size: 0.75rem;
    }
}

/* 文章区域顶部导航（采用固定命名，避免与全局类冲突） */
.nav-header {
    position: sticky;
    top: 0;
    z-index: 1200;
    background: rgba(255, 255, 255, 0.82);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.nav-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
    gap: 14px;
    padding: 10px 16px;
}

.brand-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #111827;
}

.brand-btn:hover {
    opacity: .9;
}

.brand-mark {
    width: 36px;
    height: 36px;
    border-radius: 50%;
}

.brand-title {
    font-weight: 700;
    letter-spacing: .3px;
    font-size: 1.05rem;
}

.nav-center-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
}

.nav-chips {
    display: flex;
    align-items: center;
    gap: 6px;
    overflow-x: auto;
    padding-bottom: 2px;
}

.chip {
    cursor: pointer;
}

.nav-search {
    width: 100%;
    max-width: 520px;
}

.nav-right {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #111827;
}

.author-text {
    font-weight: 600;
    letter-spacing: .3px;
}
</style>